import type {
  CreateDataSyncJobRequest,
  CreateDataSyncJobResponse,
} from './validations/types';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { queryKeysDataSyncJob } from './queryKeys';
import { createDataSyncJobSchema } from './validations';

/**
 * Creates a new data sync job.
 */
export const createDataSyncJob = async (
  data: CreateDataSyncJobRequest,
): Promise<CreateDataSyncJobResponse> => {
  // Validate input
  const validatedData = createDataSyncJobSchema.parse(data);

  const response = await vestigasApi
    .post('data_sync/sync_job', {
      json: validatedData,
    })
    .json<CreateDataSyncJobResponse>();

  return response;
};

/**
 * React Query mutation hook for creating a data sync job.
 * Automatically invalidates the jobs list query on success.
 *
 * @param options - Additional mutation options
 */
export const useMutationCreateJob = (
  options?: Omit<
    UseMutationOptions<
      CreateDataSyncJobResponse,
      Error,
      CreateDataSyncJobRequest
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationFn: createDataSyncJob,
    async onSuccess() {
      // Invalidate jobs list to refetch
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSyncJob.base(),
      });

      ToastService.success('Sync-Job erfolgreich erstellt');
    },
  });

  return mutation;
};
